import { useState, useEffect, useCallback } from 'react';
import type { DiaryEntry, CreateDiaryEntryRequest, UpdateDiaryEntryRequest } from '../types/admin.types';
import {
  fetchAdminDiaryEntries,
  createDiaryEntry,
  updateDiaryEntry,
  deleteDiaryEntry,
} from '../api/admin.api';

type UseAdminEntriesResult = {
  entries: DiaryEntry[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  create: (data: CreateDiaryEntryRequest) => Promise<DiaryEntry>;
  update: (id: string, data: UpdateDiaryEntryRequest) => Promise<DiaryEntry>;
  remove: (id: string) => Promise<void>;
  isSubmitting: boolean;
};

export const useAdminEntries = (): UseAdminEntriesResult => {
  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchAdminDiaryEntries();
      setEntries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił błąd podczas ładowania wpisów');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const create = useCallback(async (data: CreateDiaryEntryRequest): Promise<DiaryEntry> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const newEntry = await createDiaryEntry(data);
      setEntries((prev) => [newEntry, ...prev]);
      return newEntry;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Wystąpił błąd podczas tworzenia wpisu';
      setError(message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const update = useCallback(async (id: string, data: UpdateDiaryEntryRequest): Promise<DiaryEntry> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const updatedEntry = await updateDiaryEntry(id, data);
      setEntries((prev) => prev.map((entry) => (entry.id === id ? updatedEntry : entry)));
      return updatedEntry;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Wystąpił błąd podczas aktualizacji wpisu';
      setError(message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const remove = useCallback(async (id: string): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      await deleteDiaryEntry(id);
      setEntries((prev) => prev.filter((entry) => entry.id !== id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Wystąpił błąd podczas usuwania wpisu';
      setError(message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  useEffect(() => {
    void fetchEntries();
  }, [fetchEntries]);

  return {
    entries,
    isLoading,
    error,
    refetch: fetchEntries,
    create,
    update,
    remove,
    isSubmitting,
  };
};
